import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { vatApi } from "../api/vat";

const VatContext = createContext(undefined);

export function VatProvider({ children }) {
    // Default to 'Exc' (excluding VAT) - toggle OFF
    const [vatMode, setVatMode] = useState('Exc');
    const [vatRate, setVatRate] = useState(0);
    const [loading, setLoading] = useState(true);

    // Fetch the active VAT rate on mount
    useEffect(() => {
        const fetchVatRate = async () => {
            try {
                const response = await vatApi.getActiveVatRate();
                if (response.success && response.data) {
                    setVatRate(response.data.vatRate || 0);
                }
            } catch (error) {
                console.error('Error fetching VAT rate:', error);
                setVatRate(0);
            } finally {
                setLoading(false);
            }
        };

        fetchVatRate();
    }, []);

    /**
     * Toggle VAT mode between 'Inc' (including VAT) and 'Exc' (excluding VAT)
     * @param {string} mode - 'Inc' or 'Exc'
     */
    const setVat = (mode) => {
        if (mode === 'Inc' || mode === 'Exc') {
            setVatMode(mode);
        }
    };

    // Debug: log when vatMode changes
    useEffect(() => {
    }, [vatMode, vatRate]);

    /**
     * Calculate price with VAT applied (if VAT mode is 'Inc')
     * Assumes the base price is EXCLUDING VAT
     * @param {number} price - Base price (excluding VAT)
     * @returns {number} - Price with or without VAT based on current mode
     */
    const calculatePrice = (price) => {
        if (!price || isNaN(price)) return 0;
        
        const numPrice = parseFloat(price);
        
        if (vatMode === 'Inc') {
            // Add VAT to the price
            return numPrice * (1 + vatRate / 100);
        }
        
        // Return price without VAT
        return numPrice;
    };

    /**
     * Calculate price from a VAT-inclusive price
     * Use this when the original price already includes VAT
     * @param {number} priceIncVat - Price including VAT
     * @returns {number} - Price based on current mode
     */
    const calculatePriceFromIncVat = (priceIncVat) => {
        if (!priceIncVat || isNaN(priceIncVat)) return 0;
        
        const numPrice = parseFloat(priceIncVat);
        
        if (vatMode === 'Inc') {
            // Return as-is (already includes VAT)
            return numPrice;
        }
        
        // Remove VAT from the price
        return numPrice / (1 + vatRate / 100);
    };

    /**
     * Format price with currency symbol
     * @param {number} price - Price to format
     * @param {string} currency - Currency symbol (default: '£')
     * @returns {string} - Formatted price string
     */
    const formatPrice = (price, currency = '£') => {
        if (!price || isNaN(price)) return `${currency}0.00`;
        return `${currency}${parseFloat(price).toFixed(2)}`;
    };

    /**
     * Calculate and format price in one call
     * @param {number} price - Base price (excluding VAT)
     * @param {string} currency - Currency symbol (default: '£')
     * @returns {string} - Formatted price string
     */
    const getDisplayPrice = (price, currency = '£') => {
        const calculatedPrice = calculatePrice(price);
        return formatPrice(calculatedPrice, currency);
    };

    /**
     * Get VAT amount for a price
     * @param {number} price - Base price (excluding VAT)
     * @returns {number} - VAT amount
     */
    const getVatAmount = (price) => {
        if (!price || isNaN(price)) return 0;
        return parseFloat(price) * (vatRate / 100);
    };

    return (
        <VatContext.Provider
            value={{
                vatMode,
                vatRate,
                loading,
                isVatIncluded: vatMode === 'Inc',
                setVat,
                calculatePrice,
                calculatePriceFromIncVat,
                formatPrice,
                getDisplayPrice,
                getVatAmount,
            }}
        >
            {children}
        </VatContext.Provider>
    );
}

VatProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export function useVat() {
    const context = useContext(VatContext);
    if (context === undefined) {
        throw new Error("useVat must be used within a VatProvider");
    }
    return context;
}
